// A message block (FR-047; research R14): info / success / warning / error from the `in`, `ok`, `wn`
// and `danger` on `dangerSoft` token pairs. Errors are alerts; the rest are polite status messages.
import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";

import { Text } from "./Text";

export type MessageTone = "info" | "success" | "warning" | "error";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    box: {
      borderRadius: t.radius.softPanel,
      paddingVertical: t.space.smd,
      paddingHorizontal: t.space.md,
      gap: t.space.xxs,
    },
    info: { backgroundColor: t.color.infoSoft },
    success: { backgroundColor: t.color.successSoft },
    warning: { backgroundColor: t.color.warningSoft },
    error: { backgroundColor: t.color.dangerSoft },
    infoText: { color: t.color.info },
    successText: { color: t.color.success },
    warningText: { color: t.color.warning },
    errorText: { color: t.color.danger },
  }),
);

export type InlineMessageProps = {
  tone: MessageTone;
  children: string;
  /** A second line, for example the support reference. */
  detail?: string | null;
  /** Actions shown under the text (for example Resend email). */
  action?: ReactNode;
  testID?: string;
};

export function InlineMessage({
  tone,
  children,
  detail,
  action,
  testID,
}: InlineMessageProps) {
  const styles = useStyles();
  const textStyle = styles[`${tone}Text`];
  const live =
    tone === "error"
      ? {
          role: "alert" as const,
          accessibilityLiveRegion: "assertive" as const,
        }
      : { role: "status" as const, accessibilityLiveRegion: "polite" as const };
  return (
    <View style={[styles.box, styles[tone]]} {...live} testID={testID}>
      <Text weight="medium" style={textStyle}>
        {children}
      </Text>
      {detail ? (
        <Text size="sm" style={textStyle}>
          {detail}
        </Text>
      ) : null}
      {action}
    </View>
  );
}
